import { useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { RichContentRenderer } from "@/components/charts";
import type { ChatLogEntry, RichContent } from "@/app/api/types/chat-sessions";
import { translateToolName } from "@/lib/i18n-tools";
import { MarkdownTable } from "./MarkdownTable";
import { resolveAiText } from "@/lib/ai-text";

const REMARK_PLUGINS = [remarkGfm];
const MD_COMPONENTS: Components = { table: MarkdownTable };

interface ProcessingBubbleProps {
  readonly logs: ChatLogEntry[];
  readonly isStatsMode: boolean;
  readonly onSendMessage?: (message: string) => void;
}

// Core/plumbing tools hidden from UI
const HIDDEN_TOOLS = new Set([
  "dispatch_workflows",
  "get_current_task",
  "get_resource",
  "get_workflow_info",
  "complete_and_next_task",
  "list_resources",
  "output",
  "pause_workflow",
  "request_user_input",
  "start_next_task",
  "thinking",
]);

export interface StepItem {
  key: string;
  label: string;
  done: boolean;
  richContent?: RichContent;
}

export type ChronoItem =
  | { kind: "step"; step: StepItem }
  | { kind: "text"; key: string; content: string };

type TFunc = ReturnType<typeof useTranslation>["t"];

/** Build the set of completed tool names from logs. */
function buildCompletedTools(logs: ChatLogEntry[]): Set<string> {
  const completed = new Set<string>();
  for (const log of logs) {
    if (log.type === "tool_result" && log.tool_name) {
      completed.add(log.tool_name);
    }
  }
  return completed;
}

/** Add or update a tool step. Tools are flat and keyed by name. */
function processToolEvent(
  evt: string,
  toolName: string,
  steps: StepItem[],
  seenKeys: Set<string>,
  completedTools: Set<string>,
  t: TFunc,
): void {
  if (HIDDEN_TOOLS.has(toolName)) return;
  const key = `tool-${toolName}`;
  if (seenKeys.has(key)) {
    if (evt === "tool_done") {
      const existing = steps.find((s) => s.key === key);
      if (existing) existing.done = true;
    }
    return;
  }
  seenKeys.add(key);
  steps.push({
    key,
    label: translateToolName(toolName, t),
    done: evt === "tool_done" || completedTools.has(toolName),
  });
}

/**
 * Process a workflow_step log entry into steps.
 *
 * Only the sub-workflow TOOL events surface in the chat bubble. The
 * dispatch_start / dispatch_done workflow markers are intentionally dropped
 * here so sub-workflow tools collapse into the same flat, grouped tool list as
 * the main agent's instead of being fragmented by interleaved marker rows. The
 * markers remain available in the admin execution-logs view.
 */
function processWorkflowStep(
  log: ChatLogEntry,
  steps: StepItem[],
  seenKeys: Set<string>,
  completedTools: Set<string>,
  t: TFunc,
): void {
  const evt = log.event ?? "";
  if ((evt === "tool_start" || evt === "tool_done") && log.tool_name) {
    processToolEvent(evt, log.tool_name, steps, seenKeys, completedTools, t);
  }
}

/** Process a tool_start log entry into steps. */
function processToolStart(
  log: ChatLogEntry,
  logs: ChatLogEntry[],
  steps: StepItem[],
  seenKeys: Set<string>,
  completedTools: Set<string>,
  t: TFunc,
): void {
  if (!log.tool_name) return;
  const before = steps.length;
  processToolEvent(
    "tool_start",
    log.tool_name,
    steps,
    seenKeys,
    completedTools,
    t,
  );
  if (steps.length > before) {
    const resultLog = logs.find(
      (l) => l.type === "tool_result" && l.tool_name === log.tool_name,
    );
    if (resultLog?.rich_content) {
      steps[steps.length - 1].richContent = resultLog.rich_content;
    }
  }
}

export function deriveItems(
  logs: ChatLogEntry[],
  isStatsMode: boolean,
  t: TFunc,
): { items: ChronoItem[]; steps: StepItem[] } {
  const steps: StepItem[] = [];
  const items: ChronoItem[] = [];
  const seenKeys = new Set<string>();
  const completedTools = buildCompletedTools(logs);
  let textIdx = 0;

  for (const log of logs) {
    const before = steps.length;
    if (log.type === "workflow_step") {
      processWorkflowStep(log, steps, seenKeys, completedTools, t);
    } else if (log.type === "tool_start") {
      processToolStart(log, logs, steps, seenKeys, completedTools, t);
    } else if (!isStatsMode && log.type === "text" && log.content) {
      items.push({
        kind: "text",
        key: `text-${textIdx++}`,
        content: log.content,
      });
      continue;
    } else {
      continue;
    }
    for (let i = before; i < steps.length; i++) {
      items.push({ kind: "step", step: steps[i] });
    }
  }

  // Attach rich content from tool_result logs to corresponding tool steps.
  // Tools added via the workflow_step path don't go through processToolStart's
  // inline lookup, so without this pass stats-mode charts/tables would never
  // render for nested tools.
  for (const log of logs) {
    if (log.type === "tool_result" && log.tool_name && log.rich_content) {
      const step = steps.find((s) => s.key === `tool-${log.tool_name}`);
      if (step && !step.richContent) {
        step.richContent = log.rich_content;
      }
    }
  }

  return { items, steps };
}

type RenderRow =
  | { kind: "text"; key: string; content: string }
  | { kind: "step"; step: StepItem }
  | { kind: "tool-group"; key: string; steps: StepItem[] };

function isGroupable(step: StepItem, isStatsMode: boolean): boolean {
  if (!step.done) return false;
  // Keep stats-mode tools with rich content visible (don't hide charts/tables).
  if (isStatsMode && step.richContent) return false;
  return true;
}

function groupRows(items: ChronoItem[], isStatsMode: boolean): RenderRow[] {
  const out: RenderRow[] = [];
  let buffer: StepItem[] = [];
  const flush = () => {
    if (buffer.length >= 2) {
      out.push({
        kind: "tool-group",
        key: `grp-${buffer[0].key}`,
        steps: buffer,
      });
    } else if (buffer.length === 1) {
      out.push({ kind: "step", step: buffer[0] });
    }
    buffer = [];
  };
  for (const item of items) {
    if (item.kind === "step" && isGroupable(item.step, isStatsMode)) {
      buffer.push(item.step);
    } else {
      flush();
      out.push(item);
    }
  }
  flush();

  // If the last row is a trailing tool-group (no newer activity yet), peel
  // the most recent tool out so the spinner can ride on it until something
  // newer is appended.
  const last = out[out.length - 1];
  if (last && last.kind === "tool-group") {
    const popped = last.steps[last.steps.length - 1];
    const remaining = last.steps.slice(0, -1);
    if (remaining.length >= 2) {
      out[out.length - 1] = {
        kind: "tool-group",
        key: last.key,
        steps: remaining,
      };
    } else {
      out[out.length - 1] = { kind: "step", step: remaining[0] };
    }
    out.push({ kind: "step", step: popped });
  }

  return out;
}

function StepRow({
  step,
  isStatsMode,
  showSpinner,
}: {
  readonly step: StepItem;
  readonly isStatsMode: boolean;
  readonly showSpinner?: boolean;
}) {
  return (
    <div className="flex flex-col items-start gap-1 w-full">
      <div className="inline-flex items-center gap-2 text-sm text-muted-foreground rounded-full px-3 py-1">
        {showSpinner ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
        ) : (
          <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
        )}
        <Wrench className="h-3 w-3 flex-shrink-0" />
        <span>{step.label}</span>
      </div>
      {isStatsMode && step.done && step.richContent && (
        <div className="bg-card border rounded-lg p-4 overflow-hidden w-full">
          <RichContentRenderer content={step.richContent} />
        </div>
      )}
    </div>
  );
}

function ToolGroupRow({ steps }: { readonly steps: StepItem[] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="flex flex-col items-start gap-1 w-full">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-full px-3 py-1"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
        )}
        <Wrench className="h-3 w-3 flex-shrink-0" />
        <span>{t("ai.toolsUsed", { count: steps.length })}</span>
      </button>
      {expanded && (
        <div className="flex flex-col items-start gap-1 w-full">
          {steps.map((step) => (
            <StepRow key={step.key} step={step} isStatsMode={false} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ProcessingItems({
  items,
  isStatsMode,
  groupTools = true,
  showRunningSpinner = true,
}: {
  readonly items: ChronoItem[];
  readonly isStatsMode: boolean;
  readonly groupTools?: boolean;
  readonly showRunningSpinner?: boolean;
}) {
  const { t } = useTranslation();
  const rows: RenderRow[] = groupTools
    ? groupRows(items, isStatsMode)
    : items.map((item) =>
        item.kind === "text"
          ? { kind: "text", key: item.key, content: item.content }
          : { kind: "step", step: item.step },
      );

  // Spinner sits on the most recent step row until something newer is
  // appended after it (another step, a text log, or a tool group). In
  // stats mode, a rendered rich-content card counts as appended content,
  // so suppress the spinner when the last step's chart/table is visible.
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const lastStepShowsRichContent =
    lastRow !== null &&
    lastRow.kind === "step" &&
    isStatsMode &&
    lastRow.step.done &&
    Boolean(lastRow.step.richContent);
  const showSpinnerKey =
    showRunningSpinner &&
    lastRow !== null &&
    lastRow.kind === "step" &&
    !lastStepShowsRichContent
      ? lastRow.step.key
      : null;

  return (
    <>
      {rows.map((row) => {
        if (row.kind === "text") {
          // Render streamed assistant text as Markdown — mirroring the final
          // MessageBubble — so it doesn't show raw `**bold**` while streaming
          // and then flip to rendered once the turn completes.
          return (
            <div
              key={row.key}
              className="prose prose-sm max-w-none w-full min-w-0 dark:prose-invert break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre]:overflow-x-auto [&_pre]:max-w-full [&_img]:max-w-full [&_img]:h-auto [&_a]:[overflow-wrap:anywhere] [&_code]:[overflow-wrap:anywhere]"
            >
              <Markdown
                remarkPlugins={REMARK_PLUGINS}
                components={MD_COMPONENTS}
              >
                {resolveAiText(row.content, t)}
              </Markdown>
            </div>
          );
        }
        if (row.kind === "tool-group") {
          return <ToolGroupRow key={row.key} steps={row.steps} />;
        }
        return (
          <StepRow
            key={row.step.key}
            step={row.step}
            isStatsMode={isStatsMode}
            showSpinner={row.step.key === showSpinnerKey}
          />
        );
      })}
    </>
  );
}

export function ProcessingBubble({
  logs,
  isStatsMode,
  onSendMessage,
}: ProcessingBubbleProps) {
  const { t } = useTranslation();
  const { items } = deriveItems(logs, isStatsMode, t);

  return (
    <div className="flex flex-col items-start gap-1 max-w-[85%]">
      <ProcessingItems items={items} isStatsMode={isStatsMode} />

      {items.length === 0 && (
        <div className="inline-flex items-center gap-2 text-muted-foreground bg-muted/50 rounded-full px-3 py-1.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span className="text-sm">
            {isStatsMode ? t("ai.analyzing") : t("ai.thinking")}
          </span>
        </div>
      )}
    </div>
  );
}
